import React from 'react';
import SEOHead from '../components/SEOHead';
import SummaryBar from '../components/dashboard/SummaryBar';
import OilMarketPanel from '../components/dashboard/OilMarketPanel';
import MaritimePanel from '../components/dashboard/MaritimePanel';
import AirTrafficPanel from '../components/dashboard/AirTrafficPanel';
import NewsFeedPanel from '../components/dashboard/NewsFeedPanel';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
  getFAQSchema,
  getArticleSchema,
} from '../utils/seoSchemas';

/**
 * MiddleEastImpactDashboard Page
 * Single-stop live dashboard showing Middle East conflict impacts
 */
const MiddleEastImpactDashboard = () => {
  const title = 'Iran Conflict Dashboard 2026 — Oil, Gold, Shipping & Global Trade';
  const description =
    'Track real-time Iran conflict updates and their impact on oil prices, gold, Strait of Hormuz shipping, stock markets, and global trade on TraderadarGlobal.';

  // Comprehensive keywords list
  const keywords = [
    // Primary - Conflict & Geopolitics
    'Iran conflict',
    'Iran war updates',
    'Middle East conflict 2026',
    'Iran Israel tensions',
    'Iran US tensions',
    'Middle East war news',
    'Gulf crisis impact',
    // Primary - Oil & Energy
    'oil price today',
    'Iran conflict oil impact',
    'Brent crude price live',
    'WTI crude price live',
    'oil market volatility',
    'oil supply risk',
    'oil trade disruption',
    'global oil price impact',
    'oil futures Iran conflict',
    // Primary - Gold & Safe Haven
    'gold price today',
    'Iran conflict gold impact',
    'gold safe haven 2026',
    'gold market volatility',
    'gold trading news',
    'silver price today',
    'safe haven metals conflict',
    'gold vs oil today',
    'gold ETFs Middle East risk',
    // Primary - Maritime & Strait of Hormuz
    'Strait of Hormuz shipping',
    'Hormuz oil trade',
    'Hormuz supply disruption',
    'shipping through Hormuz',
    'Gulf tanker traffic',
    'maritime risk Middle East',
    // Primary - Global Trade & Economy
    'global trade disruption',
    'Middle East trade impact',
    'Iran economy impact',
    'GCC economy news',
    'global markets affected by Iran',
    'trade routes impacted by war',
    'global energy crisis',
    // Primary - Stock Markets
    'markets react to Iran war',
    'stock market volatility',
    'oil stocks Iran conflict',
    'S&P 500 impact oil prices',
    'European markets oil risk',
    'emerging market oil risk',
    // Primary - Consumer & Price Effects
    'gasoline price increase',
    'fuel price spike',
    'how Iran war affects gas prices',
    'inflation Iran conflict',
    'global commodity prices',
    'gold and oil price correlation',
    // Primary - Travel & Aviation
    'flight disruptions Middle East',
    'airline delays Iran war',
    'airspace closure Gulf',
    'Emirates flight impact',
    // Secondary Keywords
    'effect of Iran conflict on oil supply',
    '20% of world oil through Hormuz',
    'crude oil export routes',
    'tanker insurance cost spike',
    'LNG market impact Iran war',
    'Gulf air traffic disruptions',
    'commodity markets reaction',
    'gold safe haven price surge',
    'Iran sanctions effect on oil & gold',
    'maritime insurance cost surge',
    'global supply chain disruption',
  ].join(', ');

  // Enhanced breadcrumb schema with full path
  const breadcrumbItems = [
    { name: 'Home', path: '/' },
    { name: 'Iran Conflict', path: '/middle-east-impact-dashboard' },
    { name: 'Oil Prices', path: '/middle-east-impact-dashboard#oil' },
    { name: 'Gold & Safe Haven', path: '/middle-east-impact-dashboard#gold' },
    { name: 'Trade Routes', path: '/middle-east-impact-dashboard#shipping' },
    { name: 'Global Impact', path: '/middle-east-impact-dashboard#markets' },
  ];

  // FAQ schema for long-tail keywords
  const faqData = [
    {
      question: 'Will oil prices go up because of Iran war?',
      answer: 'Yes, oil prices typically surge during Middle East conflicts, especially when tensions involve Iran. The Strait of Hormuz handles 20% of global oil supply, and any disruption can cause significant price volatility. Track real-time Brent and WTI crude prices on this dashboard.',
    },
    {
      question: 'How Iran conflict affects gold and silver prices?',
      answer: 'Gold and silver prices often rise during geopolitical conflicts as investors seek safe-haven assets. The Iran conflict can trigger gold price surges as traders hedge against oil price volatility and market uncertainty. Monitor live gold and silver prices here.',
    },
    {
      question: 'Is the Strait of Hormuz safe for shipping today?',
      answer: 'Track real-time Strait of Hormuz shipping traffic and risk levels on this dashboard. The strait is critical for global oil trade, with 20% of world oil passing through. Current maritime risk levels and tanker traffic are monitored continuously.',
    },
    {
      question: 'How Iran war affects gasoline prices near me?',
      answer: 'Iran conflict impacts global oil prices, which directly affects gasoline prices worldwide. When Brent and WTI crude prices surge due to Middle East tensions, consumers typically see higher fuel costs within days. Monitor oil price changes and their impact on global markets here.',
    },
    {
      question: 'What is the impact of Iran conflict on global trade routes?',
      answer: 'The Iran conflict can disrupt critical shipping lanes through the Strait of Hormuz, affecting global trade routes, maritime insurance costs, and supply chains. Track real-time shipping disruptions, tanker traffic, and trade route impacts on this dashboard.',
    },
  ];

  // Article schema for enhanced SEO
  const articleSchema = getArticleSchema({
    seo_title: title,
    seo_description: description,
    title: 'Iran Conflict Dashboard 2026',
    description: description,
    path: '/middle-east-impact-dashboard',
    url: 'https://traderadarglobal.com/middle-east-impact-dashboard',
    created_at: new Date().toISOString(),
    dateModified: new Date().toISOString(),
    image: 'https://traderadarglobal.com/api/og-image/middle-east-dashboard.png',
  });

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema(breadcrumbItems),
    getFAQSchema(faqData),
    articleSchema,
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background">
      <SEOHead
        title={title}
        description={description}
        path="/middle-east-impact-dashboard"
        keywords={keywords}
        jsonLd={jsonLd}
        image="/api/og-image/middle-east-dashboard.png"
        imageAlt="Iran Conflict Dashboard - Live tracking of oil prices, gold, Strait of Hormuz shipping, and global trade impacts"
      />

      {/* Sticky Summary Bar */}
      <SummaryBar />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-2">
            Iran Conflict Oil Impact — Live Market & Trade Dashboard
          </h1>
          <p className="text-slate-600 dark:text-on-surface-variant text-lg">
            Track real-time Iran conflict updates and their impact on oil prices, gold, Strait of Hormuz shipping, stock markets, and global trade. Monitor Brent crude, WTI crude, gold safe-haven demand, and maritime risk as Middle East tensions affect global energy markets.
          </p>
        </header>

        {/* Dashboard Panels Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Oil & Market Panel */}
          <div className="lg:col-span-2" id="oil">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-on-surface mb-4">
              Oil & Energy Market Impact
            </h2>
            <h3 className="text-xl font-semibold text-slate-700 dark:text-on-surface-variant mb-3">
              Brent Crude & WTI Price Today
            </h3>
            <OilMarketPanel />
          </div>

          {/* Maritime Panel */}
          <div id="shipping">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-on-surface mb-4">
              Strait of Hormuz Shipping & Trade Routes
            </h2>
            <MaritimePanel />
          </div>

          {/* Air Traffic Panel */}
          <div id="aviation">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-on-surface mb-4">
              Gulf Air Traffic & Flight Disruptions
            </h2>
            <AirTrafficPanel />
          </div>
        </div>

        {/* Gold & Safe Haven Section */}
        <div className="mb-8" id="gold">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-on-surface mb-4">
            Gold & Safe Haven Assets
          </h2>
          <h3 className="text-xl font-semibold text-slate-700 dark:text-on-surface-variant mb-3">
            Gold Price Surge & Safe Haven Demand
          </h3>
          <p className="text-slate-600 dark:text-on-surface-variant mb-4">
            Gold and silver prices react to Iran conflict as investors seek safe-haven assets. Track gold price today, gold safe haven 2026 trends, and how Middle East tensions drive gold market volatility.
          </p>
        </div>

        {/* Global Markets Section */}
        <div className="mb-8" id="markets">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-on-surface mb-4">
            Global Markets & Stock Impact
          </h2>
          <p className="text-slate-600 dark:text-on-surface-variant mb-4">
            Monitor how markets react to Iran war, including stock market volatility, S&P 500 impact from oil prices, European markets oil risk, and emerging market oil risk. Track global trade disruption and Middle East trade impact on financial markets.
          </p>
        </div>

        {/* News Feed Panel - Full Width */}
        <div className="mb-8">
          <NewsFeedPanel />
        </div>

      </div>
    </div>
  );
};

export default MiddleEastImpactDashboard;

